<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="loading" kind="loading"></report-state>
    <report-state v-else-if="!rows.length" icon="mdi-account-cash-outline" :title="emptyTitle" :text="emptyText"></report-state>

    <div v-else class="rd-card rd-card--scroll">
      <table class="rd-tbl rd-tbl--workers">
        <thead>
          <tr>
            <th scope="col" class="rd-col-num">#</th>
            <th scope="col" :aria-sort="ariaSort('name')">
              <button type="button" @click="sortBy('name')">{{ $t("employee") }} {{ sortArrow("name") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('orders')">
              <button type="button" @click="sortBy('orders')">{{ $t("number_of_orders") }} {{ sortArrow("orders") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('ordered')">
              <button type="button" @click="sortBy('ordered')">{{ $t("ordered") }} {{ sortArrow("ordered") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('taken')">
              <button type="button" @click="sortBy('taken')">{{ $t("taken") }} {{ sortArrow("taken") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('company')">
              <button type="button" @click="sortBy('company')">{{ $t("company_paid") }}{{ currencySuffix }} {{ sortArrow("company") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('employee')">
              <button type="button" @click="sortBy('employee')">{{ $t("employee_paid") }}{{ currencySuffix }} {{ sortArrow("employee") }}</button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('total')">
              <button type="button" @click="sortBy('total')">{{ $t("total") }}{{ currencySuffix }} {{ sortArrow("total") }}</button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="row.key">
            <td class="rd-num rd-muted">{{ index + 1 }}</td>
            <td class="is-name" :title="row.name">
              <span class="rd-person">
                <span class="rd-avatar rd-avatar--sm">{{ row.initials }}</span>
                <span class="rd-person__name">{{ row.name }}</span>
              </span>
            </td>
            <td class="is-right rd-num">{{ row.orders }}</td>
            <td class="is-right rd-num">{{ row.ordered }}</td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.taken }">{{ row.taken }}</td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.company }">{{ money(row.company) }}</td>
            <td class="is-right rd-num" :class="{ 'is-zero': !row.employee }">{{ money(row.employee) }}</td>
            <td class="is-right rd-num"><b>{{ money(row.total) }}</b></td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td>{{ $t("total") }}</td>
            <td class="is-right rd-num">{{ sums.orders }}</td>
            <td class="is-right rd-num">{{ sums.ordered }}</td>
            <td class="is-right rd-num">{{ sums.taken }}</td>
            <td class="is-right rd-num">{{ money(sums.company) }}</td>
            <td class="is-right rd-num">{{ money(sums.employee) }}</td>
            <td class="is-right rd-num">{{ money(sums.total) }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabEmployeeMealSummary from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabEmployeeMealSummary.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";

const initialsOf = (name) =>
  name
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0))
    .slice(0, 2)
    .join("");

/**
 * Pregled obroka po radnicima firme "više restorana" na računaru: po radniku broj narudžbi, poručeno i preuzeto,
 * koliko je platila firma, a koliko radnik doplatio. Restorane i period čita iz zajedničke trake, pretragu po
 * imenu radnika takođe. Nasleđuje učitavanje, štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopMultiEmployeeMealSummary",
  extends: CompanyMultiRestaurantTabEmployeeMealSummary,
  mixins: [hasMultiFeed, hasTableSort],
  components: { ReportState, ReportTools },
  data: () => ({
    sort: { key: "name", dir: 1 },
  }),
  computed: {
    busy() {
      return this.loading;
    },
    hasRows() {
      return this.rows.length > 0;
    },
    currencySuffix() {
      return this.majorityRestaurantCurrency ? ` (${this.majorityRestaurantCurrency})` : "";
    },
    /** Radnici posle pretrage po imenu. */
    rows() {
      const search = this.toLatin(String(this.$store.state.report.filters.search || "")).toLowerCase().trim();
      return this.sortRows(
        this.employeeMealSummary
          .filter((item) => !search || this.toLatin(item.employee_name).toLowerCase().includes(search))
          .map((item, index) => ({
            key: `${item.user_id}-${index}`,
            name: item.employee_name,
            initials: initialsOf(item.employee_name),
            orders: item.order_count,
            ordered: item.order_quantity,
            taken: item.order_payed_quantity,
            company: item.company_paid,
            employee: item.employee_paid,
            total: item.total,
          }))
      );
    },
    sums() {
      return this.rows.reduce(
        (sums, row) => ({
          orders: sums.orders + row.orders,
          ordered: sums.ordered + row.ordered,
          taken: sums.taken + row.taken,
          company: sums.company + row.company,
          employee: sums.employee + row.employee,
          total: sums.total + row.total,
        }),
        { orders: 0, ordered: 0, taken: 0, company: 0, employee: 0, total: 0 }
      );
    },
    emptyTitle() {
      const search = this.$store.state.report.filters.search;
      return search ? this.$t("report_mobile.no_results_for", { search }) : this.$t("no_data");
    },
    emptyText() {
      return this.$store.state.report.filters.search ? "" : this.$t("select_date_and_filters");
    },
    kpis() {
      return [
        { key: "workers", label: this.$t("report_desktop.workers"), value: this.rows.length, tone: "inverse" },
        { key: "ordered", label: this.$t("ordered"), value: this.sums.ordered },
        { key: "total", label: this.$t("total"), value: this.money(this.sums.total) },
      ];
    },
  },
  methods: {
    money(value) {
      return (Number(value) || 0).toFixed(2);
    },
    async copyTable() {
      const headers = [
        this.$t("employee"),
        this.$t("number_of_orders"),
        this.$t("ordered"),
        this.$t("taken"),
        this.$t("company_paid"),
        this.$t("employee_paid"),
        this.$t("total"),
      ];
      const rows = this.rows.map((row) => [
        row.name,
        row.orders,
        row.ordered,
        row.taken,
        this.money(row.company),
        this.money(row.employee),
        this.money(row.total),
      ]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
